import React, { Suspense, lazy } from 'react';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import { useEnv } from '@zenbusiness/application-commons-env';
import Button from '@zenbusiness/zui/Button';
import Heading from '@zenbusiness/zui/Heading';
import PageLoading from '@zenbusiness/zui/PageLoading';
import { Route, Routes, Outlet, useRoutes, generatePath, Navigate } from 'react-router-dom';

import ClickstreamWrapper from '#client/ClickstreamWrapper';
import Template from '#client/components/Template';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useRedirectToLinkedAccounts from '#client/hooks/useRedirectToLinkedAccounts';
import { DEFAULT_SEGMENT } from '#client/manifest';
import { DEEP_LINK_PATH } from '#client/pages/BankingTransitionDeepLink/constants';
import NotFound from '#client/pages/NotFound';

import ContextLayout from './ContextLayout';

/* DEV-START */
const Logout = lazy(() => import(/* webpackChunkName: 'login' */ '#client/pages/Logout'));
const Callback = lazy(() => import(/* webpackChunkName: 'login' */ '#client/pages/Callback'));
const ErrorDebugging = lazy(
  () => import(/* webpackChunkName: 'login' */ '#client/pages/ErrorDebugging')
);
/* DEV-STOP */

const Landing = lazy(() => import(/* webpackChunkName: 'landing' */ '#client/pages/Landing'));
const Dashboard = lazy(() => import(/* webpackChunkName: 'dashboard' */ '#client/pages/Dashboard'));
const DashboardNotFunded = lazy(
  () => import(/* webpackChunkName: 'dashboard' */ '#client/pages/DashboardNotFunded')
);
const TransactionHistory = lazy(
  () => import(/* webpackChunkName: 'transactions-history' */ '#client/pages/TransactionHistory')
);
const Payees = lazy(() => import(/* webpackChunkName: 'payees' */ '#client/pages/Payees'));
const Statements = lazy(
  () => import(/* webpackChunkName: 'statements' */ '#client/pages/Statements')
);
const PayeeTransactionHistory = lazy(
  () =>
    import(
      /* webpackChunkName: 'payee-transaction-history' */ '#client/pages/TransactionHistory/components/PayeeTransactionHistory'
    )
);
const ConnectedAccountTransactionHistory = lazy(
  () =>
    import(
      /* webpackChunkName: 'connected-account-transaction-history' */ '#client/pages/TransactionHistory/components/ConnectedAccountTransactionHistory'
    )
);
const DebitCardTransactionHistory = lazy(
  () =>
    import(
      /* webpackChunkName: 'debit-card-transaction-history' */ '#client/pages/TransactionHistory/components/DebitCardTransactionHistory'
    )
);

const BankOfAmericaOffer = lazy(
  () => import(/* webpackChunkName: 'bank-of-america-offer' */ '#client/pages/BankOfAmericaOffer')
);
const BankingTransitionLayout = lazy(
  () => import(/* webpackChunkName: 'banking-transition' */ '#client/pages/BankingTransition')
);
const BankingOptionsStep = lazy(
  () =>
    import(
      /* webpackChunkName: 'banking-transition' */ '#client/pages/BankingTransition/steps/BankingOptionsStep'
    )
);
const BankingTransitionDeepLink = lazy(
  () =>
    import(/* webpackChunkName: 'banking-transition' */ '#client/pages/BankingTransitionDeepLink')
);

/**
 * Create fallback for components have lazy import
 *
 * @param {React.ReactNode} Component : Component with lazy import
 * @param {React.ReactNode?} fallbackComponent : Component is shown
 * utils lazy component have finished loading
 * @returns {React.ReactNode} : Component with suspense
 */
const withFallback = (Component, fallbackComponent = null) => (
  <Suspense fallback={fallbackComponent}>
    <Component />
  </Suspense>
);

function ConnectedAccountsRedirect() {
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { linkedAccountsPath } = useRedirectToLinkedAccounts(businessEntityUuid);

  return (
    <Navigate
      replace
      to={linkedAccountsPath}
    />
  );
}

// Shared by both route tables. The index route lands on the first step; Phase 3.5
// adds the mailing-address and mercury steps here.
const BANKING_TRANSITION_ROUTE_CHILDREN = [
  {
    index: true,
    element: (
      <Navigate
        replace
        to="banking-options"
      />
    )
  },
  {
    path: 'banking-options',
    element: withFallback(BankingOptionsStep, <PageLoading />)
  }
];

export const AUTHENTICATED_ROUTES = [
  {
    element: <ContextLayout />,
    children: [
      {
        path: ':businessEntityUuid/dashboard',
        key: 'dashboard',
        element: withFallback(Dashboard)
      },
      {
        path: ':businessEntityUuid/dashboard-notfunded',
        key: 'dashboard',
        element: withFallback(DashboardNotFunded)
      },
      {
        path: ':businessEntityUuid/transactions/history',
        key: 'transactions-history',
        element: withFallback(TransactionHistory)
      },
      { path: ':businessEntityUuid/payees', index: true, element: withFallback(Payees) },
      {
        path: ':businessEntityUuid/payees/:counterPartyId',
        element: withFallback(PayeeTransactionHistory)
      },
      {
        path: ':businessEntityUuid/statements',
        key: 'statements',
        element: withFallback(Statements)
      },
      {
        path: ':businessEntityUuid/connected-accounts',
        index: true,
        element: <ConnectedAccountsRedirect />
      },
      {
        path: ':businessEntityUuid/connected-accounts/:counterPartyId',
        element: withFallback(ConnectedAccountTransactionHistory)
      },
      {
        path: ':businessEntityUuid/debit-cards/:debitCardUuid',
        element: withFallback(DebitCardTransactionHistory)
      },
      {
        path: ':businessEntityUuid/bank-of-america-offer',
        element: withFallback(BankOfAmericaOffer)
      },
      {
        path: ':businessEntityUuid/transition',
        key: 'transition',
        element: withFallback(BankingTransitionLayout),
        children: BANKING_TRANSITION_ROUTE_CHILDREN
      }
    ]
  }
];

export const FederatedAppRoutes = () => useRoutes(AUTHENTICATED_ROUTES);

// Same routes minus businessEntityUuid prefix.
// To allow for new ingress in Zendash to use
export const AUTHENTICATED_ROUTES_V2 = [
  {
    element: <ContextLayout />,
    children: [
      {
        path: 'dashboard',
        key: 'dashboard',
        element: withFallback(Dashboard)
      },
      {
        path: 'dashboard-notfunded',
        key: 'dashboard',
        element: withFallback(DashboardNotFunded)
      },
      {
        path: 'transactions/history',
        key: 'transactions-history',
        element: withFallback(TransactionHistory)
      },
      { path: 'payees', index: true, element: withFallback(Payees) },
      {
        path: 'payees/:counterPartyId',
        element: withFallback(PayeeTransactionHistory)
      },
      {
        path: 'statements',
        key: 'statements',
        element: withFallback(Statements)
      },
      {
        path: 'connected-accounts',
        index: true,
        element: <ConnectedAccountsRedirect />
      },
      {
        path: 'connected-accounts/:counterPartyId',
        element: withFallback(ConnectedAccountTransactionHistory)
      },
      {
        path: 'debit-cards/:debitCardUuid',
        element: withFallback(DebitCardTransactionHistory)
      },
      {
        path: 'bank-of-america-offer',
        element: withFallback(BankOfAmericaOffer)
      },
      {
        path: 'transition',
        key: 'transition',
        element: withFallback(BankingTransitionLayout),
        children: BANKING_TRANSITION_ROUTE_CHILDREN
      }
    ]
  }
];

export const FederatedAppRoutesV2 = () => useRoutes(AUTHENTICATED_ROUTES_V2);

export default function AppRoutes() {
  return (
    <Routes>
      {/* DEV-START */}

      <Route
        element={<Callback />}
        path="/dashboard/callback"
      />
      <Route
        element={<Logout />}
        path="/dashboard/logout"
      />
      <Route
        element={<ErrorDebugging />}
        path={`${DEFAULT_SEGMENT}/testing/error`}
      />
      {/* DEV-STOP */}

      {/*
        Entity-less deep link for the wind-down emails. It sits outside <Authenticated> on purpose:
        that wrapper renders the standalone dev shell and always returns to the banking dashboard
        after login, neither of which suits a redirect-only page reached from an email. The page
        gates on authentication itself.
      */}
      <Route
        element={withFallback(BankingTransitionDeepLink, <PageLoading />)}
        path={DEEP_LINK_PATH}
      />

      {/* PUT ALL ROUTES THAT REQUIRE AUTHENTICATION INTO THIS SECTION */}
      <Route
        element={<Authenticated />}
        path={DEFAULT_SEGMENT}
      >
        <Route
          auth
          element={<Landing />}
          index
        />
        <Route element={<FederatedAppRoutes />} />
      </Route>

      {/* PUT ALL ROUTES THAT DO NOT REQUIRE AUTHENTICATION INTO THIS SECTION */}
      <Route
        element={<NotFound />}
        path="*"
      />
    </Routes>
  );
}

export function Authenticated({ children }) {
  const { authenticated, login } = useAuthentication();
  const { PARENT_SEGMENT = '/d' } = useEnv();
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  let bankingPath = '';

  if (!authenticated) {
    if (PARENT_SEGMENT === `/d`) {
      bankingPath = generatePath(':PARENT_SEGMENT/b/', { PARENT_SEGMENT });
    } else {
      bankingPath = generatePath(':PARENT_SEGMENT/:businessEntityUuid/banking', {
        PARENT_SEGMENT,
        businessEntityUuid
      });
    }
    login(bankingPath);
    return (
      <Template loading>
        <Heading
          color="error"
          size="2xl"
          weight="bold"
        >
          Not Logged In
        </Heading>
      </Template>
    );
  }

  return (
    <ClickstreamWrapper>
      <Template
        toolbarRight={
          <Button
            href="/dashboard/logout"
            variant="outlined"
          >
            Logout
          </Button>
        }
      >
        {children || <Outlet />}
      </Template>
    </ClickstreamWrapper>
  );
}
